import { useCallback, useState, ReactElement, ReactNode } from 'react'
import {
  Checkbox,
  FormControlLabel,
  Stack,
  styled,
  SxProps,
  Typography,
  TypographyProps,
} from '@mui/material'
import { useIsDesktopSize } from '$common/react-hooks/layout'
import { BottomDrawer } from '$common/bottom-drawer'
import { SettingsSection } from '$common/settings-section'
import { ErrorButton, PrimaryButton } from '$common/buttons'
import { nothing } from '$func/utils'
import { CancelOutlinedButton } from '$common/buttons/cancel-outlined-button'

const StyledBottomDrawer = styled(BottomDrawer)`
  & .MuiPaper-root {
    &[data-is-desktop='true'] {
      max-width: 450px;

      & .MuiFormControlLabel-root {
        margin: 16px 0 0;
      }

      & .MuiCheckbox-root {
        padding: 2px;
      }
    }
  }
`

const IconBox = styled('div')`
  display: flex;
  padding: 12px;
  justify-content: center;
  align-items: center;
  margin-bottom: 15px;
  margin-top: 16px;
`

const ButtonsContainer = styled('div')`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
  gap: 12px;
  & > button {
    height: 40px;
  }
  &[data-is-row='true'] {
    flex-direction: row-reverse;
    &[data-reverse-buttons='true'] {
      flex-direction: row;
    }
    justify-content: space-between;
    margin-top: 24px;
    gap: 0;

    & > button {
      height: 48px;
      width: calc(50% - 6px);
    }
  }
`

const ButtonTypography = (props: TypographyProps) => {
  const isDesktopSize = useIsDesktopSize()
  return (
    <Typography
      variant={isDesktopSize ? 'subtitle1' : 'subtitle3'}
      color="inherit"
      {...props}
    />
  )
}

export interface ConfirmationDialogProps {
  icon?: ReactElement
  title?: string
  confirmOption?: string
  cancelOption?: string
  checkboxOption?: string
  checkboxDefault?: boolean
  description?: JSX.Element | string
  alertMode?: boolean
  onConfirm: (checked?: boolean) => unknown
  onCancel?: () => unknown
  onClose?: () => unknown
  rootSx?: SxProps
  isOpen?: boolean
  reverseButtons?: boolean
  isInformDialog?: boolean
  cancelOptionIcon?: ReactNode
  confirmElement?: ReactNode
  showLoadingOnConfirm?: boolean
}

export function ConfirmationDialog({
  icon,
  title,
  description,
  confirmOption,
  cancelOption,
  onConfirm,
  onCancel,
  alertMode,
  checkboxOption,
  checkboxDefault,
  rootSx,
  isOpen,
  onClose,
  reverseButtons,
  isInformDialog,
  cancelOptionIcon,
  confirmElement,
  showLoadingOnConfirm = false,
}: ConfirmationDialogProps) {
  const [isLoading, setIsLoading] = useState(false)
  const isDesktopSize = useIsDesktopSize()
  const [checked, setChecked] = useState(checkboxDefault || false)

  const handleOnConfirm = useCallback(async () => {
    try {
      if (showLoadingOnConfirm) setIsLoading(true)
      await onConfirm(checkboxOption ? checked : undefined)
    } finally {
      setIsLoading(false)
    }
  }, [checked, onConfirm, checkboxOption, showLoadingOnConfirm])

  return (
    <StyledBottomDrawer
      isOpen={!!isOpen}
      onClose={onClose || onCancel || nothing}
      // @ts-ignore
      paperProps={{ 'data-is-desktop': isDesktopSize }}
    >
      {icon && <IconBox>{icon}</IconBox>}

      <SettingsSection
        headerText={title}
        headerVariant={
          isInformDialog ? 'paragraph2' : isDesktopSize ? 'h3' : 'subtitle1'
        }
        headerColor={
          isInformDialog
            ? 'neutral900'
            : isDesktopSize
              ? 'neutral1000'
              : 'secondary700'
        }
        headerSx={
          isInformDialog
            ? { margin: '20px 16px 16px' }
            : isDesktopSize
              ? { margin: 3, marginBottom: 2 }
              : undefined
        }
        sx={{ marginBottom: 0, ...rootSx }}
      >
        <Stack
          sx={{
            padding: isInformDialog
              ? '0 16px 16px 16px'
              : isDesktopSize
                ? '0 24px 24px'
                : '5px 20px 20px',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            variant={
              isInformDialog
                ? 'paragraph2'
                : isDesktopSize
                  ? 'body1'
                  : 'paragraph2'
            }
            color={isInformDialog ? 'neutral600' : ''}
            dir="auto"
            component="pre"
            sx={{
              overflowWrap: 'break-word',
              wordWrap: 'break-word',
              wordBreak: 'break-word',
              whiteSpace: 'pre-wrap',
              textAlign: 'right',
            }}
          >
            {description}
          </Typography>

          {checkboxOption && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={checked}
                  onChange={e => setChecked(e.target.checked)}
                  sx={{ color: 'neutral300' }}
                />
              }
              label={
                <Typography variant={isDesktopSize ? 'body1' : 'body2'}>
                  {checkboxOption}
                </Typography>
              }
            />
          )}

          {confirmElement || (
            <ButtonsContainer
              data-reverse-buttons={reverseButtons}
              data-is-row={isDesktopSize || isInformDialog}
            >
              {alertMode ? (
                <ErrorButton
                  text={<ButtonTypography>{confirmOption}</ButtonTypography>}
                  onClick={handleOnConfirm}
                  loading={isLoading}
                />
              ) : (
                <PrimaryButton
                  text={<ButtonTypography>{confirmOption}</ButtonTypography>}
                  onClick={handleOnConfirm}
                  loading={isLoading}
                />
              )}

              {cancelOption && (
                <CancelOutlinedButton
                  sx={{ color: 'primary500', borderColor: 'primary300' }}
                  text={<ButtonTypography>{cancelOption}</ButtonTypography>}
                  onClick={onCancel || nothing}
                  startIcon={cancelOptionIcon}
                  disabled={isLoading}
                />
              )}
            </ButtonsContainer>
          )}
        </Stack>
      </SettingsSection>
    </StyledBottomDrawer>
  )
}

ConfirmationDialog.defaultProps = {
  icon: null,
  title: '',
  cancelOption: '',
  onCancel: nothing,
  checkboxOption: '',
  description: '',
  alertMode: false,
  checkboxDefault: false,
  rootSx: null,
}
